import { useMemo } from 'react'

import { useModals, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import CreateOrEditTrackingLinkModal from '@modals/CreateOrEditTrackingLinkModal'

import { TrackingLinkModel } from '@domain-group/realhub-backend-api-types'

import PageContent from './components/PageContent'

export type PageContextPayload = {
  callbacks: {
    showCreateOrEditTrackingLinkModal: () => void,
  },
}

const defaultState = {
  showCreateOrEditTrackingLinkModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

/* eslint-disable no-undef */
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrEditTrackingLinkModal: {
      closeModal: () => setState({ showCreateOrEditTrackingLinkModal: false }),
      createTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateTrackingLink: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const List = () => {
  const [state, setState] = useSetState(defaultState)
  const { showCreateOrEditTrackingLinkModal } = state

  const modalContext = useModals({ setState })
  const { callbacks: { setModalData } } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateOrEditTrackingLinkModal: (payload: { trackingLink: TrackingLinkModel }) => {
        setModalData('CreateOrEditTrackingLinkModal', payload)
        setState({ showCreateOrEditTrackingLinkModal: true })
      },
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent />

        <CreateOrEditTrackingLinkModal
          callbacks={callbacks('CreateOrEditTrackingLinkModal', setState)}
          showModal={showCreateOrEditTrackingLinkModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default List
